import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { TrackingProvider } from "@/components/TrackingProvider";
import { ProtectedAdminRoute } from "./components/admin/ProtectedAdminRoute";
import { useInitFacebookPixel } from "./hooks/useInitFacebookPixel";
import { useInitClarity } from "./hooks/useInitClarity";
import { useDynamicBranding } from "./hooks/useDynamicBranding";

// Lazy load route components for better code splitting
const Home = lazy(() => import("./pages/Home"));
const Index = lazy(() => import("./pages/Index"));
const LandingPage = lazy(() => import("./pages/LandingPageOptimized"));
const LandingPage2 = lazy(() => import("./pages/LandingPage2"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminProducts = lazy(() => import("./pages/AdminProducts"));
const AdminAnalyticsUnified = lazy(() => import("./pages/AdminAnalyticsUnified"));
const AdminAnalyticsPro = lazy(() => import("./pages/AdminAnalyticsPro"));
const AdminAnalyticsEnhanced = lazy(() => import("./pages/AdminAnalyticsEnhanced"));
const AdminAnalyticsMaster = lazy(() => import("./pages/AdminAnalyticsMaster"));
const AdminProductAnalytics = lazy(() => import("./pages/AdminProductAnalytics"));
const AdminPixelSettings = lazy(() => import("./pages/AdminPixelSettings"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminUpsell = lazy(() => import("./pages/AdminUpsell"));
const AdminDownsell = lazy(() => import("./pages/AdminDownsell"));
const AdminAbandonedOrders = lazy(() => import("./pages/AdminAbandonedOrders"));
const AdminSiteSettings = lazy(() => import("./pages/AdminSiteSettings"));
const AdminBrandSettings = lazy(() => import("./pages/AdminBrandSettings"));
const AdminHomeBuilder = lazy(() => import("./pages/AdminHomeBuilder"));
const AdminProductFAQs = lazy(() => import("./pages/AdminProductFAQs"));
const AdminProductReviews = lazy(() => import("./pages/AdminProductReviews"));
const AdminABTesting = lazy(() => import("./pages/AdminABTesting"));
const AdminABTestingDetails = lazy(() => import("./pages/AdminABTestingDetails"));
const ABTestRouter = lazy(() => import("./pages/ABTestRouter"));
const AdminCustomLandingPages = lazy(() => import("./pages/AdminCustomLandingPages"));
const AdminCustomLandingBuilder = lazy(() => import("./pages/AdminCustomLandingBuilder"));
const AdminLandingAnalytics = lazy(() => import("./pages/AdminLandingAnalytics"));
const AdminFunnels = lazy(() => import("./pages/AdminFunnels"));
const AdminFunnelBuilder = lazy(() => import("./pages/AdminFunnelBuilder"));
const AdminFunnelAnalytics = lazy(() => import("./pages/AdminFunnelAnalytics"));
const AdminMobileBarSettings = lazy(() => import("./pages/AdminMobileBarSettings"));
const AdminMarketing = lazy(() => import("./pages/AdminMarketing"));
const AdminCheckoutSettings = lazy(() => import("./pages/AdminCheckoutSettings"));
const CustomLandingPage = lazy(() => import("./pages/CustomLandingPage"));
const Upsell = lazy(() => import("./pages/Upsell"));
const Downsell = lazy(() => import("./pages/Downsell"));
const ProductPageOptimized = lazy(() => import("./pages/ProductPageOptimized"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const Products = lazy(() => import("./pages/Products"));
const ProductDetails = lazy(() => import("./pages/ProductDetails"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const ThankYouPage = lazy(() => import("./pages/ThankYouPage"));
const Checkout = lazy(() => import("./pages/Checkout"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Forma3 = lazy(() => import("./pages/Forma3"));
const Forma2 = lazy(() => import("./pages/Forma2"));
const FormaCheckout = lazy(() => import("./pages/FormaCheckout"));
const FormaThankYou = lazy(() => import("./pages/FormaThankYou"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CartPage = lazy(() => import("./pages/CartPage"));
const queryClient = new QueryClient();

// Component to initialize hooks that need QueryClient
const AppInitializers = () => {
  useInitFacebookPixel();
  useInitClarity();
  useDynamicBranding();
  return null;
};
const App = () => {
  return <QueryClientProvider client={queryClient}>
      <AppInitializers />
      <TooltipProvider>
        <CartProvider>
          <Toaster />
          
          <BrowserRouter>
            <TrackingProvider>
              <Suspense fallback={<div style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>جاري التحميل...</div>}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/index" element={<Index />} />
                <Route path="/bolt1" element={<LandingPage />} />
                <Route path="/bolt2" element={<LandingPage2 />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<ProtectedAdminRoute><Admin /></ProtectedAdminRoute>} />
                <Route path="/admin/orders" element={<ProtectedAdminRoute><AdminOrders /></ProtectedAdminRoute>} />
                <Route path="/admin/products" element={<ProtectedAdminRoute><AdminProducts /></ProtectedAdminRoute>} />
                <Route path="/admin/analytics-unified" element={<ProtectedAdminRoute><AdminAnalyticsUnified /></ProtectedAdminRoute>} />
                <Route path="/admin/analytics-pro" element={<ProtectedAdminRoute><AdminAnalyticsPro /></ProtectedAdminRoute>} />
                <Route path="/admin/analytics-enhanced" element={<ProtectedAdminRoute><AdminAnalyticsEnhanced /></ProtectedAdminRoute>} />
                <Route path="/admin/analytics" element={<ProtectedAdminRoute><AdminAnalyticsMaster /></ProtectedAdminRoute>} />
                <Route path="/admin/product-analytics/:id" element={<ProtectedAdminRoute><AdminProductAnalytics /></ProtectedAdminRoute>} />
                <Route path="/admin/landing-analytics/:id" element={<ProtectedAdminRoute><AdminLandingAnalytics /></ProtectedAdminRoute>} />
                <Route path="/admin/pixel-settings" element={<ProtectedAdminRoute><AdminPixelSettings /></ProtectedAdminRoute>} />
                <Route path="/admin/settings" element={<ProtectedAdminRoute><AdminSettings /></ProtectedAdminRoute>} />
                <Route path="/admin/upsell" element={<ProtectedAdminRoute><AdminUpsell /></ProtectedAdminRoute>} />
                <Route path="/admin/downsell" element={<ProtectedAdminRoute><AdminDownsell /></ProtectedAdminRoute>} />
                <Route path="/admin/abandoned-orders" element={<ProtectedAdminRoute><AdminAbandonedOrders /></ProtectedAdminRoute>} />
                <Route path="/admin/site-settings" element={<ProtectedAdminRoute><AdminSiteSettings /></ProtectedAdminRoute>} />
                <Route path="/admin/brand-settings" element={<ProtectedAdminRoute><AdminBrandSettings /></ProtectedAdminRoute>} />
                <Route path="/admin/home-builder" element={<ProtectedAdminRoute><AdminHomeBuilder /></ProtectedAdminRoute>} />
                <Route path="/admin/product-faqs" element={<ProtectedAdminRoute><AdminProductFAQs /></ProtectedAdminRoute>} />
                <Route path="/admin/product-reviews" element={<ProtectedAdminRoute><AdminProductReviews /></ProtectedAdminRoute>} />
                <Route path="/admin/ab-testing" element={<ProtectedAdminRoute><AdminABTesting /></ProtectedAdminRoute>} />
                <Route path="/admin/ab-testing/:id" element={<ProtectedAdminRoute><AdminABTestingDetails /></ProtectedAdminRoute>} />
                <Route path="/admin/custom-landing-pages" element={<ProtectedAdminRoute><AdminCustomLandingPages /></ProtectedAdminRoute>} />
                <Route path="/admin/landing-builder/:id" element={<ProtectedAdminRoute><AdminCustomLandingBuilder /></ProtectedAdminRoute>} />
                <Route path="/admin/funnels" element={<ProtectedAdminRoute><AdminFunnels /></ProtectedAdminRoute>} />
                <Route path="/admin/funnels/:id" element={<ProtectedAdminRoute><AdminFunnelBuilder /></ProtectedAdminRoute>} />
                <Route path="/admin/funnels/:id/analytics" element={<ProtectedAdminRoute><AdminFunnelAnalytics /></ProtectedAdminRoute>} />
                <Route path="/admin/mobile-bar-settings" element={<ProtectedAdminRoute><AdminMobileBarSettings /></ProtectedAdminRoute>} />
                <Route path="/admin/marketing" element={<ProtectedAdminRoute><AdminMarketing /></ProtectedAdminRoute>} />
                <Route path="/admin/checkout-settings" element={<ProtectedAdminRoute><AdminCheckoutSettings /></ProtectedAdminRoute>} />
                <Route path="/ab/:experimentId" element={<ABTestRouter />} />
                <Route path="/lp/:slug" element={<CustomLandingPage />} />
                <Route path="/upsell" element={<Upsell />} />
                <Route path="/downsell" element={<Downsell />} />
                <Route path="/bolt3" element={<ProductPageOptimized />} />
                <Route path="/products" element={<Products />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/product/:id" element={<ProductDetails />} />
                <Route path="/shop/checkout" element={<CheckoutPage />} />
                <Route path="/shop/thank-you" element={<ThankYouPage />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/thank-you" element={<ThankYou />} />
                <Route path="/forma-3" element={<Forma3 />} />
                <Route path="/forma-2" element={<Forma2 />} />
                <Route path="/forma-checkout" element={<FormaCheckout />} />
                <Route path="/forma-thank-you" element={<FormaThankYou />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </TrackingProvider>
          </BrowserRouter>
        </CartProvider>
      </TooltipProvider>
    </QueryClientProvider>;
};
export default App;